/**
 * Whether motion runs at all on this device, on this screen.
 *
 * A pure function so the rule can be tested without a browser — the same reason the
 * spatial-navigation rule is pure. Everything else in this folder is effects and
 * teardown; this is the decision, and the decision is where the mistakes are.
 *
 * Two answers rather than one, because they fail differently. `animate` gates
 * scroll-driven and entrance tweens. `smooth` gates Lenis owning the page scroll — a
 * heavier commitment, since it replaces a browser behaviour the rest of the app builds on.
 */
import type { DeviceProfile } from '../device/useDeviceProfile';

export interface MotionInputs {
  device: DeviceProfile;
  reducedMotion: boolean;
  pathname: string;
}

export interface MotionDecision {
  /** Scroll-driven and entrance animations. */
  animate: boolean;
  /** Lenis drives the page scroll. Implies `animate`. */
  smooth: boolean;
}

const OFF: MotionDecision = { animate: false, smooth: false };

export function decideMotion({ device, reducedMotion, pathname }: MotionInputs): MotionDecision {
  // The player is full-bleed and does not scroll. A RAF loop running beside a video
  // decoder costs frames and buys nothing, and on a phone it costs battery during the
  // longest-running screen in the app.
  if (pathname.startsWith('/watch/')) return OFF;

  // Not a preference to weigh against the design — it is an accessibility setting, and
  // vestibular disorders are the reason it exists.
  if (reducedMotion) return OFF;

  /*
   * The television gets neither, and this is a correctness rule rather than a taste one.
   *
   * Spatial navigation picks the next focus by measuring element rectangles at the moment
   * a key is pressed (see `layouts/tv/focus/geometry.ts`). An element that is mid-tween is
   * not where it will be, so a right-press during an entrance animation can score the
   * wrong neighbour — and the failure is intermittent, which is the worst kind to own on a
   * device you debug over sdb.
   *
   * Lenis is worse still: `FocusContext.focus()` moves the ring with `scrollIntoView`,
   * which writes the native scroll position directly. Lenis would animate back from it and
   * the focused card would drift out from under the ring.
   *
   * The TV already has a coherent motion language in CSS transitions on focus, which cost
   * nothing and cannot desynchronise from a measurement.
   */
  if (device === 'tv') return OFF;

  return { animate: true, smooth: true };
}
